import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const Section = styled.section`
  padding: 120px 2rem;
  background: #000000;
  position: relative;

  /* Full-width Top Line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 1px;
    background: #333333;
    pointer-events: none;
  }

  /* Full-width Bottom Line */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100vw;
    height: 1px;
    background: #333333;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1350px;
  margin: 0 auto;
  width: 100%;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8rem;
  align-items: start;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 3rem;
  }
`;

const LeftTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
  font-weight: 500;
`;

const Square = styled.div`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const RightContent = styled.div`
  width: 100%;
`;

const MainHeading = styled.h2`
  font-size: 2rem;
  color: #ffffff;
  margin: 0 0 1rem 0;
  font-weight: 800;
  line-height: 1.1;
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #cbd5e1;
  line-height: 1.6;
  margin: 0;
  max-width: 90%;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const CarouselWrapper = styled.div`
  margin-top: 4rem;
`;

const CarouselContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 2rem 0;
  cursor: grab;
  user-select: none;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &:active {
    cursor: grabbing;
  }

  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-right: 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: #0b0b0bff;
  border: 1px solid #2a2a2a;
  padding: 3rem;
  min-width: 420px;
  max-width: 520px;
  flex-shrink: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 0.7s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  border-radius: 0; /* Sharp corners */

  @keyframes fadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 1024px) {
    min-width: 360px;
  }

  @media (max-width: 768px) {
    min-width: 320px;
    padding: 2.5rem 2rem;
  }
`;

const Quote = styled.p`
  font-size: 18px;
  font-weight: 500;
  line-height: 1.55;
  color: #e2e8f0;
  margin: 0;
`;

const ScrollHint = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 0.95rem;
  margin-top: 2rem;
  opacity: 0.8;
  pointer-events: none;
`;

const ClientsSection = () => {
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const testimonials = [
    { id: 1, quote: "Working with this team transformed our hiring process. Their attention to detail and deep understanding of our culture helped us find perfect candidates who’ve become core team members." },
    { id: 2, quote: "Unmatched professionalism and industry insight. They delivered exceptional talent perfectly aligned with our vision and values." },
    { id: 3, quote: "Outstanding service. Their executive search helped us land a visionary leader who drove massive growth in just six months." },
    { id: 4, quote: "They truly understood our luxury brand DNA and delivered candidates who live and breathe our values." },
    { id: 5, quote: "Data-driven, insightful, and incredibly efficient. The quality of candidates was beyond what we thought possible." },
    { id: 6, quote: "A true partnership. Their network and expertise in digital transformation are world-class." },
  ];

  const duplicatedTestimonials = [...testimonials, ...testimonials];

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const x = e.pageX - carouselRef.current.offsetLeft;
    setStartX(x);
    setScrollLeft(carouselRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUpOrLeave = () => {
    setIsDragging(false);
  };

  return (
    <Section id="clients">
      <Container>
        <TopRow>
          <LeftTitle>
            <Square />
            <SectionTitle>Clients</SectionTitle>
          </LeftTitle>

          <RightContent>
            <MainHeading>What the world says</MainHeading>
            <SubHeading>
              Why we are trusted by industry leaders worldwide
            </SubHeading>
          </RightContent>
        </TopRow>

        <CarouselWrapper>
          <CarouselContainer
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUpOrLeave}
            onMouseLeave={handleMouseUpOrLeave}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              setStartX(touch.pageX - carouselRef.current.offsetLeft);
              setScrollLeft(carouselRef.current.scrollLeft);
              setIsDragging(true);
            }}
            onTouchMove={(e) => {
              if (!isDragging) return;
              const touch = e.touches[0];
              const walk = (touch.pageX - carouselRef.current.offsetLeft - startX) * 2;
              carouselRef.current.scrollLeft = scrollLeft - walk;
            }}
            onTouchEnd={handleMouseUpOrLeave}
          >
            {duplicatedTestimonials.map((t, i) => (
              <TestimonialCard
                key={`${t.id}-${i}`}
                delay={`${(i % testimonials.length) * 0.15}s`}
              >
                <Quote>“{t.quote}”</Quote>
              </TestimonialCard>
            ))}
          </CarouselContainer>
        </CarouselWrapper>

        <ScrollHint>Drag to explore more testimonials</ScrollHint>
      </Container>
    </Section>
  );
};

export default ClientsSection;